import { connect } from 'formik';
import React, { Component, PropsWithChildren, Fragment } from 'react';
import { RegisterElement } from '..';
import { FieldHelpersWrapper } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import './CheckboxesGroup.scss';
import { CheckboxLabel } from './CheckboxLabel';
import { IChkGroupProps, IChkOption, InnerCheckboxGroupProps } from './types';

/**
 * Custom controlled Input Pattern: renders a group of checkboxes.
 * All checkboxes must have different names, and no explicit value (they produce boolean value).
 * Use ``<CheckboxesGroup />`` when you need to validate all checkboxes at once and
 * provide a single error message per group.
 */
export class CheckboxesGroup extends Component<PropsWithChildren<InnerCheckboxGroupProps>, {}> {
  private id = this.props.id || createId('checkboxGroup');

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);
  }

  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
  }

  public maybeAutoGenerateList() {
    if (this.props.render || !this.props.options) {
      return null;
    }
    const markup = this.props.options.map((item: IChkOption) => {
      return <Fragment key={item.name}>{this.renderCheckbox(item)}</Fragment>;
    });
    return markup;
  }

  public maybeGenerateFromRender() {
    if (!this.props.render) {
      return null;
    }
    return this.props.render({ renderCheckbox: this.renderCheckbox });
  }

  public renderCheckbox = (option: IChkOption) => {
    const props = resolveProps(this.props, this.props.formik);
    const checked = props.value ? props.value.indexOf(option.name) > -1 : false;
    const id = option.id || `${this.id}-${option.name}`;
    return (
      <CheckboxLabel inline={this.props.inline}>
        <input
          id={id}
          name={option.name}
          type={'checkbox'}
          checked={checked}
          onChange={this.onChkChange}
          disabled={option.disabled}
          aria-invalid={!!props.error}
          onBlur={this.props.formik && this.props.formik.handleBlur && this.props.formik.handleBlur(this.props.name)}
        />{' '}
        {option.label}
      </CheckboxLabel>
    );
  };

  public onChkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const allProps = resolveProps(this.props, this.props.formik);
    const value = allProps.value.slice();
    const ind = value.indexOf(e.target.name);
    if (e.target.checked && ind === -1) {
      value.push(e.target.name);
    }
    if (!e.target.checked && ind > -1) {
      value.splice(ind, 1);
    }
    this.handleChange(value);
  };

  public handleChange(value: string[]) {
    if (this.props.onChange) {
      this.props.onChange(this.props.name, value);
      return;
    }
    if (this.props.formik && this.props.formik.setFieldValue) {
      this.props.formik.setFieldValue(this.props.name, value);
    }
  }

  public render() {
    const props = resolveProps(this.props, this.props.formik);

    return (
      <RegisterElement name={`register-${this.id}`}>
        {(ref: any) => {
          const myRef = props.error ? ref : undefined;
          return (
            <div ref={myRef} id={`${this.id}-wrapper`} className={'hlc-checkboxgroup'} qa-id={this.props['qa-id']}>
              <FieldHelpersWrapper id={this.id} props={this.props} useGroupLabel={true}>
                {this.maybeAutoGenerateList()}
                {this.maybeGenerateFromRender()}
              </FieldHelpersWrapper>
            </div>
          );
        }}
      </RegisterElement>
    );
  }
}

export default connect<IChkGroupProps>(CheckboxesGroup);
